import { kittingMaterialScanTarget } from "@/pages/manufacturing/kittingScanModel";

export type TransferPackQuantityKeyEffect =
  | { type: "quantity-digit"; digit: string; buffer: string; rewindDigits: number }
  | { type: "quantity-backspace"; buffer: string }
  | { type: "hold"; buffer: string; rewindDigits: number }
  | { type: "submit-quantity"; buffer: string }
  | { type: "scan"; scanned: string }
  | { type: "ignore"; buffer: string };

function numpadDigit(key: string): string | null {
  const match = /^Numpad(\d)$/.exec(key);
  return match?.[1] ?? null;
}

/**
 * Quantity mode still has to accept a container label. A wedge types the hub
 * `/i/{id}` URL (or a SKU) and ends with Enter. Leading digits must not become
 * the carton quantity, and Enter must scan that label instead of submitting qty.
 */
export function applyTransferPackQuantityKey(
  buffer: string,
  key: string,
): TransferPackQuantityKeyEffect {
  const padDigit = numpadDigit(key);
  if (padDigit != null) {
    return { type: "quantity-digit", digit: padDigit, buffer: "", rewindDigits: 0 };
  }

  if (key === "Backspace") {
    if (buffer.length === 0) {
      return { type: "quantity-backspace", buffer: "" };
    }
    const next = buffer.slice(0, -1);
    if (/^\d+$/.test(buffer)) {
      return { type: "quantity-backspace", buffer: next };
    }
    return { type: "hold", buffer: next, rewindDigits: 0 };
  }

  if (key === "Enter") {
    const scanned = buffer.trim();
    if (scanned !== "" && kittingMaterialScanTarget(scanned) != null) {
      return { type: "scan", scanned };
    }
    if (buffer.length > 0) {
      return { type: "ignore", buffer: "" };
    }
    return { type: "submit-quantity", buffer: "" };
  }

  if (key.length !== 1) {
    return { type: "ignore", buffer };
  }

  const next = `${buffer}${key}`;
  if (/^\d+$/.test(next)) {
    return { type: "quantity-digit", digit: key, buffer: next, rewindDigits: 0 };
  }

  const rewindDigits = /^\d+$/.test(buffer) ? buffer.length : 0;
  return { type: "hold", buffer: next, rewindDigits };
}

export function transferPackContainerIdentityLabel(
  containers: readonly { short_code: string; quantity: number }[] | null | undefined,
): string | null {
  const rows = containers ?? [];
  if (rows.length === 0) return null;
  if (rows.length === 1) {
    return `Container ${rows[0]!.short_code}`;
  }
  return `Containers ${rows.map((row) => `${row.short_code} (${String(row.quantity)})`).join(", ")}`;
}
